import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apprenticeApi } from '../api/apprentices';
import { instructorApi } from '../api/instructors';
import { trackingApi } from '../api/tracking';
import { Apprentice, OTJRecord, Instructor } from '../types';
import { format } from 'date-fns';

export const ApprenticeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, userRole } = useAuth();
  const [apprentice, setApprentice] = useState<Apprentice | null>(null);
  const [otjRecords, setOtjRecords] = useState<OTJRecord[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedInstructorId, setSelectedInstructorId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const loadData = useCallback(async () => {
    if (!user?.tenant_id || !id) return;
    try {
      const [apprenticeRes, otjRes, instructorsRes] = await Promise.all([
        apprenticeApi.getOne(user.tenant_id, id),
        trackingApi.getOTJRecords(user.tenant_id, id),
        userRole === 'administrator' ? instructorApi.getAll(user.tenant_id) : Promise.resolve({ data: [] }),
      ]);
      
      setApprentice(apprenticeRes.data);
      setOtjRecords(otjRes.data);
      setInstructors(instructorsRes.data);
      setSelectedInstructorId(apprenticeRes.data.instructor_id || '');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id, id, userRole]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssignInstructor = async () => {
    if (!user?.tenant_id || !id || !selectedInstructorId) {
      setAssignError('Please select an assessor');
      return;
    }

    try {
      setAssigning(true);
      setAssignError('');
      
      // Find the selected instructor to get their canvas_user_id
      const instructor = instructors.find(i => i.id === selectedInstructorId);
      if (!instructor) {
        setAssignError('Invalid assessor selection');
        return;
      }

      await apprenticeApi.assignInstructor(user.tenant_id, id, instructor.canvas_user_id);
      await loadData();
      setShowAssignModal(false);
    } catch (err: any) {
      setAssignError(err.response?.data?.message || 'Failed to assign assessor');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;
  if (!apprentice) return <div className="p-8">Not found</div>;

  const totalHours = otjRecords.reduce((sum, r) => sum + Number(r.hours), 0);
  const assignedInstructor = instructors.find(i => i.canvas_user_id === apprentice.instructor_id);

  return (
    <div>
      <Link to="/apprentices" className="text-blue-600 mb-4 inline-block">
        ← Back to Apprentices
      </Link>
      
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">
          {apprentice.first_name} {apprentice.last_name}
        </h2>
        {userRole === 'administrator' && (
          <button
            onClick={() => setShowAssignModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            {apprentice.instructor_id ? 'Change Assessor' : 'Assign Assessor'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded shadow p-4">
          <div className="text-sm text-gray-500">Total Hours</div>
          <div className="text-2xl font-bold">{totalHours.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded shadow p-4">
          <div className="text-sm text-gray-500">Activities</div>
          <div className="text-2xl font-bold">{otjRecords.length}</div>
        </div>
        <div className="bg-white rounded shadow p-4">
          <div className="text-sm text-gray-500">Progress</div>
          <div className="text-2xl font-bold">{Math.round(apprentice.otj_percentage)}%</div>
        </div>
      </div>

      <div className="bg-white rounded shadow p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Information</h3>
        <div className="space-y-2">
          <p className="text-sm">
            <span className="font-medium">Email:</span> {apprentice.email}
          </p>
          {apprentice.employer_name && (
            <p className="text-sm">
              <span className="font-medium">Employer:</span> {apprentice.employer_name}
            </p>
          )}
          {assignedInstructor ? (
            <p className="text-sm">
              <span className="font-medium">Assigned Assessor:</span>{' '}
              {assignedInstructor.first_name} {assignedInstructor.last_name}
              <span className="text-gray-500 ml-2">({assignedInstructor.email})</span>
            </p>
          ) : (
            userRole === 'administrator' && (
              <p className="text-sm text-orange-600">
                ⚠ No assessor assigned to this apprentice
              </p>
            )
          )}
          <p className="text-sm">
            <span className="font-medium">Status:</span>{' '}
            <span className={`px-2 py-1 rounded-full text-xs ${
              apprentice.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {apprentice.status}
            </span>
          </p>
        </div>
      </div>

      <div className="bg-white rounded shadow p-6">
        <h3 className="text-lg font-semibold mb-4">OTJ Records</h3>
        {otjRecords.length === 0 ? (
          <p className="text-gray-600">No OTJ records yet</p>
        ) : (
          <div className="space-y-4">
            {otjRecords.map(r => (
              <div key={r.id} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{r.activity_type}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        r.verification_status === 'approved' 
                          ? 'bg-green-100 text-green-800'
                          : r.verification_status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {r.verification_status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{r.description}</p>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-sm text-gray-600">
                      {format(new Date(r.activity_date), 'dd/MM/yyyy')}
                    </div>
                    <div className="text-lg font-bold text-blue-600 mt-1">
                      {r.hours}h
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">
              {apprentice.instructor_id ? 'Change Assessor' : 'Assign Assessor'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Assessor
              </label>
              <select
                value={selectedInstructorId}
                onChange={(e) => setSelectedInstructorId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select an assessor --</option>
                {instructors.map((instructor) => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.first_name} {instructor.last_name} ({instructor.email})
                  </option>
                ))}
              </select>
              {instructors.length === 0 && (
                <p className="mt-2 text-sm text-orange-600">
                  No assessors available. Please add assessors first.
                </p>
              )}
            </div>

            {assignError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{assignError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setAssignError('');
                  setSelectedInstructorId(assignedInstructor?.id || '');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={assigning}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignInstructor}
                disabled={assigning || !selectedInstructorId || instructors.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {assigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
